import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, Play, Trash2, PackagePlus, Boxes } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const STATUS = {
  scheduled_draft: ["Draft ready", "sr-badge-gray"], submitted_for_go_live: ["Submitted for go-live", "sr-badge-amber"],
  live: ["Live", "sr-badge-green"], paused_oos: ["Paused · out of stock", "sr-badge-amber"],
  budget_capped: ["Budget cap reached", "sr-badge-gray"], cancelled: ["Cancelled", "sr-badge-gray"],
};

export default function DynamicProductAds() {
  const [cfg, setCfg] = useState(null);
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState({ enabled: false, monthly_cap: 3000, per_ad_monthly: 300, objective: "sales" });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    sr.dynamicAdsSettings().then((c) => { setCfg(c); setForm({ enabled: c.enabled, monthly_cap: c.monthly_cap, per_ad_monthly: c.per_ad_monthly, objective: c.objective }); }).catch(() => setCfg({}));
    sr.dynamicAds().then((r) => setAds(r.ads || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    const next = { ...form, ...patch };
    setForm(next);
    try { const c = await sr.setDynamicAdsSettings(next); setCfg(c); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try { const r = await sr.runDynamicAds(); toast[r.ok ? "success" : "message"](r.ok ? `Built ${r.created} ad(s)` : (r.message || "Nothing to build")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run"); } finally { setRunning(false); }
  };
  const cancel = async (id) => { try { await sr.deleteDynamicAd(id); toast.success("Cancelled"); load(); } catch { toast.error("Could not cancel"); } };

  if (!cfg) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;
  const committed = (cfg.active_ads || 0) * (form.per_ad_monthly || 0);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="dynamic-ads-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Wand2 size={22} /> Dynamic Product Ads
      </h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        Turn this on and every new or restocked product automatically gets an AI-written ad — all kept within a monthly budget you set. Auto-built ads arrive as drafts in the go-live queue, so nothing spends without your approval.
      </p>

      <div className="sr-card" style={{ marginTop: 14 }} data-testid="dynamic-settings">
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="dynamic-enable" />
          <strong style={{ color: "var(--navy)" }}>{form.enabled ? "On — auto-building product ads" : "Off"}</strong>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginTop: 12 }}>
          <label style={{ fontSize: 13, color: "var(--navy)" }}>Monthly budget cap
            <input type="number" min={0} className="sr-input sr-input-sm" value={form.monthly_cap} onChange={(e) => setForm((s) => ({ ...s, monthly_cap: Number(e.target.value) }))} onBlur={() => save({})} data-testid="dynamic-cap" />
          </label>
          <label style={{ fontSize: 13, color: "var(--navy)" }}>Budget per product / month
            <input type="number" min={0} className="sr-input sr-input-sm" value={form.per_ad_monthly} onChange={(e) => setForm((s) => ({ ...s, per_ad_monthly: Number(e.target.value) }))} onBlur={() => save({})} data-testid="dynamic-per-ad" />
          </label>
          <label style={{ fontSize: 13, color: "var(--navy)" }}>Objective
            <select className="sr-input sr-input-sm" value={form.objective} onChange={(e) => save({ objective: e.target.value })} data-testid="dynamic-objective">
              <option value="sales">Sales</option><option value="traffic">Traffic</option><option value="awareness">Awareness</option>
            </select>
          </label>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
          <span className="sr-muted" style={{ fontSize: 12 }}>Committed: <strong style={{ color: committed > form.monthly_cap ? "#B42318" : "var(--navy)" }}>{money(committed)}</strong> of {money(form.monthly_cap)} · {cfg.active_ads || 0} active</span>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={runNow} disabled={running || !form.enabled} data-testid="dynamic-run">{running ? <Loader2 size={13} className="sr-spin" /> : <><Play size={13} /> Build now</>}</button>
        </div>
      </div>

      {ads.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 34 }} data-testid="dynamic-empty">
          <Boxes size={26} style={{ color: "var(--muted)" }} />
          <p style={{ marginTop: 8 }}>No auto-built ads yet. Add products to your Catalog (or sync Shopify), turn this on, and we'll build an ad for each one.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {ads.map((a) => {
            const [label, cls] = STATUS[a.status] || [a.status, "sr-badge-gray"];
            return (
              <div key={a.id} className="sr-card" style={{ display: "flex", gap: 12, alignItems: "flex-start" }} data-testid={`dynamic-ad-${a.id}`}>
                {a.image_url ? <img src={a.image_url} alt="" style={{ width: 54, height: 54, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: 54, height: 54, borderRadius: 8, background: "#EEF2F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><PackagePlus size={20} style={{ color: "var(--muted)" }} /></div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ color: "var(--navy)" }}>{a.product_name}</strong>
                    <span className={`sr-badge ${cls}`} data-testid={`dynamic-status-${a.id}`}>{label}</span>
                    {a.trigger && <span className="sr-badge sr-badge-gray">{a.trigger === "restock" ? "restocked" : "new"}</span>}
                    <span className="sr-muted" style={{ fontSize: 11 }}>{money(a.monthly_budget)}/mo</span>
                  </div>
                  {a.ad_copy && <div style={{ marginTop: 4 }}><div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13 }}>{a.ad_copy.headline}</div><div className="sr-muted" style={{ fontSize: 12.5 }}>{a.ad_copy.primary_text}</div></div>}
                </div>
                {a.status !== "cancelled" && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => cancel(a.id)} data-testid={`dynamic-cancel-${a.id}`}><Trash2 size={13} /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
